import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  transcriptorModel,
  useEncounterDocumentsPolling,
} from '@/entities/transcriptor';

import { isLiveProtocolDocument } from '../lib';
import { liveSession } from '../model';

import { useLiveSession } from './useLiveSession';

const { selectConsultationById } = transcriptorModel.selectors;
const { fetchConsultationById } = transcriptorModel.thunks;

// Mounted once next to the recording widget: the live session follows the
// recording's pause and stop through the store, learns its documents from
// the encounter and reads the stored result when rendering finishes.
export const useLiveSessionFollower = () => {
  const dispatch = useAppDispatch();
  const session = useLiveSession();
  const { isRecording, isPaused } = useAppSelector(
    (root) => root.transcriptor.recording,
  );
  const encounterId = session?.meta.encounterId ?? '';
  const patientId = session?.meta.patientId ?? '';
  const hasLive = !!session?.live;
  const savedEdits = session?.savedEdits ?? 0;
  const encounter = useAppSelector((root) =>
    encounterId ? selectConsultationById(root, encounterId) : null,
  );
  const protocols = (encounter?.documents ?? []).filter(isLiveProtocolDocument);
  const documentKey = protocols.map((document) => document.id).join('\n');
  const completedKey = protocols
    .filter((document) => document.status === 'completed')
    .map((document) => document.id)
    .join('\n');
  const finalLoadsRef = useRef<string[]>([]);

  // Nothing the follower reads changes while the recording records.
  useEncounterDocumentsPolling({
    patientId,
    consultation: isRecording ? undefined : encounter ?? undefined,
  });

  useEffect(() => {
    finalLoadsRef.current = [];
    if (encounterId && patientId) {
      void dispatch(
        fetchConsultationById({ consultationId: encounterId, patientId }),
      );
    }
  }, [encounterId, patientId]);

  useEffect(() => {
    if (!hasLive) {
      return;
    }
    if (!isRecording) {
      void liveSession.stop();
      return;
    }
    if (isPaused) {
      liveSession.pause();
    } else {
      liveSession.resume();
    }
  }, [hasLive, isRecording, isPaused]);

  useEffect(() => {
    if (documentKey) {
      liveSession.setDocuments(documentKey.split('\n'));
    }
  }, [documentKey]);

  useEffect(() => {
    if (!completedKey) {
      return;
    }
    const loads = completedKey
      .split('\n')
      .filter((documentId) => !finalLoadsRef.current.includes(documentId));
    finalLoadsRef.current = [...finalLoadsRef.current, ...loads];
    loads.forEach(
      (documentId) => void liveSession.loadFinalAnswers(documentId),
    );
  }, [completedKey]);

  // An edit of a finished protocol re-renders the document and withdraws its
  // approval on the server, so the encounter is read again.
  useEffect(() => {
    if (savedEdits && encounter?.status === 'succeeded') {
      void dispatch(
        fetchConsultationById({ consultationId: encounterId, patientId }),
      );
    }
    // Only a new stored edit triggers the read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedEdits]);
};
